import { AEntity, Component, Entity, registerComponent, registerSystem, System } from "aframe";
import { Euler, Object3D, Quaternion, Vector3, Vector3Like } from "three";
import { untransformPoint } from "../three/math";



interface TransformData
{
	source : (Entity | Object3D) []; //Hack : allow passing arrays of Three.js Object3D.

	enabled : boolean;
	
	position : Vector3Like;
	positionFactor : Vector3Like;
	positionOffset : Vector3Like;
	positionStep : Vector3Like;
	minPosition : Vector3Like;
	maxPosition : Vector3Like;
	
	rotation : Vector3Like;
	rotationOrder : string;
	rotationFactor : Vector3Like;
	rotationOffset : Vector3Like;
	rotationStep : Vector3Like;
	minRotation : Vector3Like;
	maxRotation : Vector3Like;
}
interface Vector3Limits
{
	x : (v : number) => number;
	y : (v : number) => number;
	z : (v : number) => number;
}
const identity = (v : number) => v;

const isIdentity = (v : Vector3Limits) => (v.x === identity) && (v.y === identity) && (v.z === identity);

const makeLimit = (min : number, max : number) => 
{
	if(isNaN(min))
	{
		if(isNaN(max)) return identity;
		return (v : number) => (v > max) ? max : v;
	}
	if(isNaN(max))
		return (v : number) => (v < min) ? min : v;

	return (v : number) => (v < min) ? min : ((v > max) ? max : v);
}
const makePositionLimitVector = (min : Vector3Like, max : Vector3Like) =>
{
	return {
		x : makeLimit(min.x, max.x),
		y : makeLimit(min.y, max.y),
		z : makeLimit(min.z, max.z)
	};
}

const isZero = (v : Vector3Like) => !(v.x || v.y || v.z);
const isOne = (v : Vector3Like) => (v.x==1) && (v.y==1) && (v.z ==1);

const degrees = Math.PI / 180.0, twoPI = 2.0 * Math.PI;


const makeRotationLimit = (min : number, max : number) =>
{
	//having only min or max, but not both, is kind of stupid because rotations are cyclic...
	if(isNaN(min))
	{
		if(isNaN(max)) return identity;
		return (v : number) => (v > max) ? max : v;
	}
	if(isNaN(max))
		return (v : number) => (v < min) ? min : v;

	//Clamp to the full circle containing both min and max :
	const rangeMin = Math.PI * Math.floor( min / Math.PI ), rangeMax = Math.PI * Math.ceil( max / Math.PI );
	return (v : number) =>
	{
		if(v < rangeMin)
			v += twoPI * Math.ceil( (rangeMin - v) / twoPI );
		else if(v > rangeMax)
			v -= twoPI * Math.ceil( (v - rangeMax) / twoPI );
		return (v < min) ? min : (v > max) ? max : v;
	}
}
const makeRotationLimitVector = (min : Vector3Like, max : Vector3Like) =>
{
	return { 
		x : makeRotationLimit(min.x * degrees, max.x * degrees),
		y : makeRotationLimit(min.y * degrees, max.y * degrees),
		z : makeRotationLimit(min.z * degrees, max.z * degrees)
	};
}





interface TransformComponent extends Component
{
	system : TransformSystem;
	data : TransformData;
	apply() : void;
	positionLimits : Vector3Limits;
	rotationLimits : Vector3Limits;
}
interface TransformSystem extends System
{
	components : TransformComponent[];
}


const stepped = (v : number, s : number) => s * Math.floor( 0.5 + v / s );


const numberOrZero = (n : any) =>
{
	const v = Number(n);
	return isNaN(v) ? 0 : v;
}


registerComponent("transform", {
	multiple : true,
	schema : {
		source:  { type : "selectorAll" },

		enabled : { type : "boolean", default : true },
		
		position : { type : "vec3" },
		positionFactor : { type : "vec3", default : {x:1, y:1, z:1} },
		positionOffset : { type : "vec3" },
		positionStep : { type : "vec3" },
		minPosition : { type : "vec3", default: {x:NaN, y:NaN,z:NaN} },
		maxPosition : { type : "vec3", default: {x:NaN, y:NaN,z:NaN} },
		
		rotation : { type : "vec3" },
		rotationOrder : { type : "string", default : "YXZ" },
		rotationFactor : { type : "vec3", default : {x:1, y:1, z:1} },
		rotationOffset : { type : "vec3" },
		rotationStep : { type : "vec3" },
		minRotation : { type : "vec3", default: {x:NaN, y:NaN,z:NaN} },
		maxRotation : { type : "vec3", default: {x:NaN, y:NaN,z:NaN} }
	},
	init(this : TransformComponent)
	{
		this.system.components.push(this);
		this.positionLimits = { x : identity, y : identity, z : identity };
		this.rotationLimits = { x : identity, y : identity, z : identity };
	},
	update(this : TransformComponent, oldData : TransformData)
	{
		this.positionLimits = makePositionLimitVector( this.data.minPosition, this.data.maxPosition );
		this.rotationLimits = makeRotationLimitVector( this.data.minRotation, this.data.maxRotation );

		if(!this.data.enabled)
		{
			if(oldData && oldData.enabled)
			{
				const pos = oldData.position || this.data.position, rot = oldData.rotation || this.data.rotation, obj = this.el.object3D;
				
				//Reset transforms to position/rotation components or zero is the best we can do...
				const posComp = this.el.components.position, rotComp = this.el.components.rotation;
				const defPos = (posComp && posComp.data) || {};
				const defRot = (rotComp && rotComp.data) || {};
				
				pos.x && (obj.position.x = numberOrZero(defPos.x));
				pos.y && (obj.position.y = numberOrZero(defPos.y));
				pos.z && (obj.position.z = numberOrZero(defPos.z));
				rot.x && (obj.rotation.x = numberOrZero(defRot.x) * degrees);
				rot.y && (obj.rotation.y = numberOrZero(defRot.y) * degrees);
				rot.z && (obj.rotation.z = numberOrZero(defRot.z) * degrees);
			}
		}
	},
	apply(this : TransformComponent)
	{
		if( !this.data.enabled )
			return; //Off
		
		const obj = this.el.object3D;
		const src : Object3D[] = ( this.data.source || [] ).map(  elOrObj => (elOrObj instanceof AEntity) ? elOrObj.object3D : elOrObj  );
		obj.updateWorldMatrix(true, false);
		for(let srcObj of src)
			srcObj.updateWorldMatrix(true, false);

		const k = src.length ? (1.0 / src.length) : 1.0;
		const pos = this.data.position;
		if(pos.x || pos.y || pos.z)
		{
			const p = new Vector3();
			for(let srcObj of src)
			{
				const m = srcObj.matrixWorld.elements;
				p.x += m[12];
				p.y += m[13];
				p.z += m[14];
			}
			//Average :
			p.x *= k;
			p.y *= k;
			p.z *= k;

			const step = this.data.positionStep;
			if(step.x) p.x = stepped(p.x, step.x);
			if(step.y) p.y = stepped(p.y, step.y);
			if(step.z) p.z = stepped(p.z, step.z);

			if(obj.parent)
				untransformPoint(p, obj.parent.matrixWorld);
			const offset = this.data.positionOffset, factor = this.data.positionFactor;
			if(pos.x) obj.position.x = this.positionLimits.x(p.x + offset.x) * factor.x;
			if(pos.y) obj.position.y = this.positionLimits.y(p.y + offset.y) * factor.y;
			if(pos.z) obj.position.z = this.positionLimits.z(p.z + offset.z) * factor.z;
		}

		const rot = this.data.rotation;
		if(rot.x || rot.y || rot.z)
		{
			const q = new Quaternion(), e = new Euler(0,0,0, this.data.rotationOrder || obj.rotation.order);
			const pq = new Quaternion(), r = new Vector3();
			if(obj.parent)
			{
				obj.parent.matrixWorld.decompose({} as Vector3, pq, {} as Vector3);
				pq.conjugate();
				//pq.setFromRotationMatrix(obj.parent.matrixWorld).conjugate();
			}


			const offset = this.data.rotationOffset, factor = this.data.rotationFactor, step = this.data.rotationStep;
			if( (src.length == 1) && rot.x && rot.y && rot.z && isZero(offset) && isOne(factor) && isZero(step) && isIdentity(this.rotationLimits) )
			{
				//Simply copy source rotation : use quaternion instead of euler
				//q.setFromRotationMatrix(src[0].matrixWorld).premultiply(pq);
				src[0].matrixWorld.decompose({} as Vector3, q, {} as Vector3);
				obj.quaternion.copy(q.premultiply(pq));
			}
			else
			{
				//Specific axes / Multiple sources / offset / factor / step : use euler angles
				for(let srcObj of src)
				{
					srcObj.matrixWorld.decompose({} as Vector3, q, {} as Vector3);
					//q.setFromRotationMatrix(srcObj.matrixWorld).premultiply(pq);
					e.setFromQuaternion(q.premultiply(pq), e.order);
					r.x += e.x;
					r.y += e.y;
					r.z += e.z;
				}

				//Average : 
				r.x *= k;
				r.y *= k;
				r.z *= k;
				
				if(step.x) r.x = stepped(r.x, step.x * degrees);
				if(step.y) r.y = stepped(r.y, step.y * degrees);
				if(step.z) r.z = stepped(r.z, step.z * degrees);
				
				obj.rotation.set(
					rot.x ? this.rotationLimits.x(r.x + offset.x * degrees) * factor.x : obj.rotation.x,
					rot.y ? this.rotationLimits.y(r.y + offset.y * degrees) * factor.y : obj.rotation.y,
					rot.z ? this.rotationLimits.z(r.z + offset.z * degrees) * factor.z : obj.rotation.z
				);	
	
			}
		}

		obj.updateWorldMatrix(false, true);
	},
	remove(this : TransformComponent)
	{
		const ix = this.system.components.indexOf(this);
		if(ix >= 0) 
			this.system.components.splice(ix, 1);
	}

});

registerSystem("transform", {

	init(this : TransformSystem)
	{
		this.components = [];
	},
	tick(this : TransformSystem)
	{
		for(let comp of this.components)
			comp.apply();

	}

});